/**
 * Date utilities. All functions are pure and timezone-respectful — they
 * operate on the user's *local* time (since paydays and budget periods
 * are inherently local concepts), and ISO date strings of the form
 * "YYYY-MM-DD" represent local calendar days, not UTC instants.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Return a new Date set to 00:00:00.000 local time on the same day. */
export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Return a new Date set to 23:59:59.999 local time on the same day. */
export function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Whole local-calendar days between a and b (b - a). Negative if b < a. */
export function diffInDays(a: Date, b: Date): number {
  const aMs = startOfDay(a).getTime();
  const bMs = startOfDay(b).getTime();
  return Math.round((bMs - aMs) / MS_PER_DAY);
}

/** "YYYY-MM-DD" in local time. */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse "YYYY-MM-DD" as a local-time Date at 00:00.
 * Plain `new Date("YYYY-MM-DD")` parses as UTC, which silently shifts
 * the day for users west of UTC — this avoids that.
 */
export function fromIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Last calendar day of the given (1-indexed) month. */
export function lastDayOfMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** Clamp `day` into the legal range for that month (handles 31 in Feb). */
export function clampDayToMonth(year: number, month1: number, day: number): number {
  return Math.min(Math.max(1, day), lastDayOfMonth(year, month1));
}

/** Returns true iff `d` is between `start` and `end` inclusive (local days). */
export function isBetweenInclusive(d: Date, start: Date, end: Date): boolean {
  const t = startOfDay(d).getTime();
  return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
}

/** "Tuesday, March 4" style. Used in headers. */
export function formatLongDate(d: Date, locale = 'en-US'): string {
  return d.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** "April 17, 2026". Used for next-payday displays in Settings. */
export function formatPayDate(d: Date, locale = 'en-US'): string {
  return d.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Apr 17". Compact due-date display in lists. */
export function formatPayDateShort(d: Date, locale = 'en-US'): string {
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/** Greeting based on local hour. */
export function timeOfDayGreeting(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** "5m ago" / "2h ago" / "3d ago" / "Mar 4". */
export function formatRelativeTime(
  iso: string,
  now: Date = new Date(),
  locale = 'en-US',
): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const diffMs = now.getTime() - d.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/**
 * Humanized label for a transaction's date pill.
 *   today               → "Today"
 *   yesterday           → "Yesterday"
 *   last 6 days         → weekday name ("Friday")
 *   same calendar year  → "May 5"
 *   different year      → "May 5, 2025"
 */
export function formatRelativeDate(
  iso: string,
  today: Date = new Date(),
  locale = 'en-US',
): string {
  const target = startOfDay(fromIsoDate(iso));
  const t0 = startOfDay(today);
  const elapsed = diffInDays(target, t0); // positive when target is in the past
  if (elapsed === 0) return 'Today';
  if (elapsed === 1) return 'Yesterday';
  if (elapsed > 1 && elapsed < 7) {
    return target.toLocaleDateString(locale, { weekday: 'long' });
  }
  if (target.getFullYear() === t0.getFullYear()) {
    return target.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
  return target.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
